import React, { useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router';
import { SingleColumnPageBase } from 'src/components/PageBase/SingleColumnPageBase';
import { useGetProfileSettingsQuery } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { useGetPaymentStatusQuery } from 'src/api/v2/payment-v2-ApiSlice';
import { GetProfileSettingsResponse } from 'src/model/api/v2/data-contracts';
import { BasicDetailsSection } from 'src/pages/ProfileSettingsPage/sections/BasicDetailsSection';
import { LogoSection } from 'src/pages/ProfileSettingsPage/sections/LogoSection';
import { PaymentSettingsSection } from 'src/pages/ProfileSettingsPage/sections/PaymentSettingsSection';
import { IconSetSection } from 'src/pages/ProfileSettingsPage/sections/IconSetSection';
import { ManufacturerDiscountsSection } from 'src/pages/ProfileSettingsPage/sections/ManufacturerDiscountsSection';
import { ServiceFeeSection } from 'src/pages/ProfileSettingsPage/sections/ServiceFeeSection';
import { PasswordSection } from 'src/pages/ProfileSettingsPage/sections/PasswordSection';
import { AccountActionsSection } from 'src/pages/ProfileSettingsPage/sections/AccountActionsSection';
import { ParticipantListsSection } from 'src/pages/ProfileSettingsPage/sections/ParticipantListsSection';
import { useProfileSettingsForm } from 'src/pages/ProfileSettingsPage/useProfileSettingsForm';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ts } from 'src/utils/translate';
import { DOUBLE_SPACING } from 'src/theme/style-consts';

const PAGE_CONTENT_MAX_WIDTH = 1100;

export const ProfileSettingsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { data: settings, isError } = useGetProfileSettingsQuery();

    useEffect(() => {
        if (isError) {
            dispatch(showSnackBar({
                text: ts('profile_settings_load_error', 'Failed to load profile settings'),
                severity: 'error',
            }));
        }
    }, [isError, dispatch]);

    const handleBack = () => {
        navigate(location.state?.from ?? '/projects');
    };

    return (
        <SingleColumnPageBase
            pageContent={
                <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
                    <Stack spacing={DOUBLE_SPACING} sx={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, marginX: 'auto' }}>
                        <Box>
                            <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                                {ts('q_sec_gotoback', 'Go back')}
                            </Button>
                        </Box>
                        <Typography variant="h5">
                            {ts('projects_profile_settings', 'Profile settings')}
                        </Typography>
                        {settings && <ProfileSettingsSections settings={settings} />}
                    </Stack>
                </Box>
            }
        />
    );
};

const ProfileSettingsSections = ({ settings }: { settings: GetProfileSettingsResponse }) => {
    const { data: paymentStatus } = useGetPaymentStatusQuery();
    const { form, formValidationErrors, changeField, commitForm, changeAndSave } = useProfileSettingsForm(settings.profile);
    const preferredCurrency = settings.options.currencies.find(currency => currency.id === form.preferred_currency_id);

    return (
        <>
            <ParticipantListsSection />
            <BasicDetailsSection
                form={form}
                formValidationErrors={formValidationErrors}
                options={settings.options}
                avatarFileName={settings.profile.user_avatar_link}
                showBillingNotice={paymentStatus != null && !paymentStatus.has_volume_license}
                onFieldChange={changeField}
                onFieldBlur={commitForm}
                onFieldChangeAndSave={changeAndSave}
            />
            <PaymentSettingsSection
                isProLicenseValid={settings.is_pro_license_valid}
                proExpirationDate={settings.pro_expiration_date}
            />
            <LogoSection logoFileName={settings.profile.user_company_link} />
            <ManufacturerDiscountsSection />
            <IconSetSection
                value={form.planner_icon_set_id}
                options={settings.options.icon_sets}
                onChange={(iconSetId) => changeAndSave('planner_icon_set_id', iconSetId)}
            />
            <PasswordSection />
            <AccountActionsSection />
            {settings.show_work_hour_prices && (
                <ServiceFeeSection
                    key={preferredCurrency?.name}
                    prices={settings.work_hour_prices}
                    currencyName={preferredCurrency?.name ?? ''}
                    infoLink={settings.work_hour_info_link}
                />
            )}
        </>
    );
};
